import { useEffect, useState } from 'react';

import {
  Alert,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { getTriggers, Trigger } from 'services/suggestedMessages';

import { TriggersI18nKeys } from 'components/MessageTipsDrawer/constants';
import * as S from 'components/MessageTipsDrawer/styles';

import { PreferredTemplatesPreview } from './PreferredTemplatePreview';
import { TemplatesListPreview } from './TemplatesListPreview';

import { ThemeStepProps } from '../types';

export const ThemeStep: React.FC<ThemeStepProps> = ({
  onClose,
  onNextStep,
}) => {
  const [selectedTheme, setSelectedTheme] = useState<Trigger>();
  const [isErrorSnackOpen, setIsErrorSnackOpen] = useState(false);

  const intl = useIntl();

  const handleCloseDrawer = () => {
    if (onClose) onClose();
  };

  const {
    data: triggers,
    isLoading,
    isError,
  } = useQuery({
    queryFn: () => getTriggers(),
    queryKey: ['triggers'],
  });

  useEffect(() => {
    setSelectedTheme(triggers?.[0]);
  }, [triggers]);

  useEffect(() => {
    if (isError) {
      setIsErrorSnackOpen(true);
    }
  }, [isError, setIsErrorSnackOpen]);

  const handleChangeSelectedTheme = (event: SelectChangeEvent) => {
    const triggerCode = event.target.value as string;
    const theme = triggers?.find(
      trigger => trigger.trigger_code === triggerCode,
    );

    setSelectedTheme(theme);
  };

  const handleNextStep = () => {
    if (onNextStep) onNextStep(selectedTheme?.trigger_code);
  };

  return (
    <S.Wrapper>
      <S.Header>
        <div />
        <Typography variant="overline" letterSpacing={1}>
          {intl.formatMessage({ id: 'MESSAGETIPS.GLOBAL.TITLE' })}
        </Typography>
        <IconButton onClick={handleCloseDrawer}>
          <S.CloseDrawerIcon />
        </IconButton>
      </S.Header>

      <Typography variant="body2">
        {intl.formatMessage({ id: 'MESSAGETIPS.GLOBAL.SUBTITLE' })}
      </Typography>

      <Divider
        variant="fullWidth"
        component="hr"
        aria-hidden="true"
        sx={{ my: 3 }}
      />

      <Typography variant="body2">
        {intl.formatMessage({ id: 'MESSAGETIPS.EDITSCHEDULE.SELECTTHEME' })}
      </Typography>

      <FormControl fullWidth sx={{ my: 2 }}>
        {!isLoading && (
          <Select
            value={selectedTheme?.trigger_code || ''}
            onChange={handleChangeSelectedTheme}
          >
            {triggers?.map(trigger => (
              <MenuItem key={trigger.trigger_code} value={trigger.trigger_code}>
                {intl.formatMessage({
                  id: TriggersI18nKeys[trigger.trigger_code].title,
                })}
              </MenuItem>
            ))}
          </Select>
        )}

        {isLoading && (
          <Skeleton
            variant="rounded"
            width="100%"
            height={56}
            animation="wave"
          />
        )}

        {!isLoading && selectedTheme?.partner_preference_required && (
          <PreferredTemplatesPreview
            triggerCode={selectedTheme.trigger_code}
            handleNextStep={handleNextStep}
            setIsErrorSnackOpen={setIsErrorSnackOpen}
          />
        )}

        {!isLoading && selectedTheme?.partner_preference_required === false && (
          <TemplatesListPreview
            triggerCode={selectedTheme.trigger_code}
            setIsErrorSnackOpen={setIsErrorSnackOpen}
          />
        )}

        <Snackbar
          open={isErrorSnackOpen}
          autoHideDuration={6000}
          onClose={() => setIsErrorSnackOpen(false)}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
            {intl.formatMessage({ id: 'MESSAGETIPS.GLOBALERROR.SYSTEMERROR' })}
          </Alert>
        </Snackbar>
      </FormControl>

      <S.CancelationBanner severity="info" variant="outlined">
        {intl.formatMessage({ id: 'MESSAGETIPS.GLOBAL.WARNING' })}
      </S.CancelationBanner>
    </S.Wrapper>
  );
};
